import { Box, Text, Image, Badge, Button, Link, useDisclosure, Spinner, VStack } from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import { useBreakpointsContext } from '../hooks/useBreakpointsContext';
import { usePuntosColombiaAccruals } from '../hooks/usePuntosColombiaAccruals';
import { IoHappyOutline, IoGiftOutline, IoLogoWhatsapp } from 'react-icons/io5';
import { FiDollarSign, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import palommaPlusLogo from '../assets/palomma-plus-logo.png';
import puntosColombiaLogo from '../assets/puntos-colombia-logo.svg';
import SubscriptionCancellationModal from './SubscriptionCancellationModal';
import PuntosColombiaRetryModal from './PuntosColombiaRetryModal';
import { PALOMMA_PLUS } from '@/data/subscriptions';

const PCO_FAILURE_MESSAGES: Record<string, string> = {
  customerNotFound:
    'No encontramos tu cuenta. Verifica que tengas una cuenta de Puntos Colombia activa o intenta con otro documento.',
  invalidAccountStatus:
    'Tu cuenta de Puntos Colombia no está activa. Contacta a Puntos Colombia o intenta con otro documento.',
};

const PCO_STATUS_BADGE: Record<'pending' | 'accrued' | 'failed', { label: string; colorScheme: string }> = {
  pending: { label: 'Pendiente', colorScheme: 'yellow' },
  accrued: { label: 'Acreditado', colorScheme: 'green' },
  failed: { label: 'Fallido', colorScheme: 'red' },
};

const formatBillingMonth = (billingMonth: string): string => {
  const [year, month] = billingMonth.split('-').map(Number);
  if (!year || !month) return billingMonth;
  const label = new Date(year, month - 1, 1).toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const VISIBLE_ACCRUALS_COUNT = 3;

const PuntosColombiaAccrualsTable = ({ variant = 'card' }: { variant?: 'inline' | 'card' }): JSX.Element | null => {
  const { accruals, isLoading, retry, refetch } = usePuntosColombiaAccruals();
  const [managingChargeId, setManagingChargeId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (isLoading || accruals.length === 0) return null;

  const hasMore = accruals.length > VISIBLE_ACCRUALS_COUNT;
  const visibleAccruals = isExpanded ? accruals : accruals.slice(0, VISIBLE_ACCRUALS_COUNT);

  const containerProps =
    variant === 'inline'
      ? { pt: 4, borderTop: '1px solid', borderColor: '#F0EFF2' }
      : { border: '1px solid', borderColor: '#DFDEE2', borderRadius: '8px', p: 4 };

  return (
    <Box display="flex" flexDirection="column" gap={3} {...containerProps}>
      <Box display="flex" alignItems="center" gap={2}>
        <Image src={puntosColombiaLogo} alt="Puntos Colombia" boxSize="24px" objectFit="contain" flexShrink={0} />
        <Text fontSize="0.875rem" fontWeight={600}>
          Puntos Colombia acumulados
        </Text>
      </Box>
      {visibleAccruals.map((accrual) => {
        const badge = PCO_STATUS_BADGE[accrual.status];
        const message = accrual.failureReason ? PCO_FAILURE_MESSAGES[accrual.failureReason] : undefined;
        return (
          <Box
            key={accrual.subscriptionChargeId}
            display="flex"
            flexDirection="column"
            gap={2}
            pt={3}
            borderTop="1px solid"
            borderColor="#F0EFF2"
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={3}>
              <Box display="flex" flexDirection="column">
                <Text fontSize="0.875rem" fontWeight={500}>
                  {formatBillingMonth(accrual.billingMonth)}
                </Text>
                <Text fontSize="0.75rem" color="#6B6A7A">
                  {accrual.amount} Puntos Colombia
                </Text>
              </Box>
              <Badge colorScheme={badge.colorScheme} fontSize="0.7rem">
                {badge.label}
              </Badge>
            </Box>
            {accrual.status === 'failed' && (
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={3}>
                {message ? (
                  <Text fontSize="0.75rem" color="#9F9DB0">
                    {message}
                  </Text>
                ) : (
                  <Box />
                )}
                <Button
                  size="xs"
                  colorScheme="purple"
                  variant="outline"
                  onClick={() => setManagingChargeId(accrual.subscriptionChargeId)}
                >
                  Gestionar
                </Button>
              </Box>
            )}
          </Box>
        );
      })}

      {hasMore && (
        <Button
          size="xs"
          variant="link"
          colorScheme="purple"
          alignSelf="center"
          rightIcon={isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {isExpanded ? 'Ver menos' : `Ver ${accruals.length - VISIBLE_ACCRUALS_COUNT} más`}
        </Button>
      )}

      <PuntosColombiaRetryModal
        isOpen={!!managingChargeId}
        onClose={() => setManagingChargeId(null)}
        onConfirm={async (input) => {
          if (!managingChargeId) return;
          await retry({ subscriptionChargeId: managingChargeId, ...input });
          setManagingChargeId(null);
        }}
      />
    </Box>
  );
};

const SaldoPalommaCard = (): JSX.Element => {
  const { creditsBalance } = useContext(InvoiceDataContext);
  const { isMobile } = useBreakpointsContext();
  const formatCurrency = useCurrencyFormatter();

  return creditsBalance > 0 ? (
    <Box border="1px solid" borderColor="#DFDEE2" borderRadius="8px" p={isMobile ? 4 : 5}>
      <VStack align="start" spacing={1}>
        <Text fontSize="0.875rem" fontWeight={600}>
          Saldo Palomma
        </Text>
        <Text fontSize="1.5rem" fontWeight={700} color="green.600">
          {formatCurrency(creditsBalance)}
        </Text>
        <Text fontSize="0.75rem" color="#6B6A7A">
          Disponible para pagar facturas o Palomma Plus.
        </Text>
      </VStack>
    </Box>
  ) : (
    <></>
  );
};

const SubscriptionTab = (): JSX.Element => {
  const { customerSubscription, cancelSubscription, isLoading, experiments } = useContext(SubscriptionsContext);
  const { isMobile } = useBreakpointsContext();
  const formatCurrency = useCurrencyFormatter();
  const { isOpen, onOpen, onClose } = useDisclosure();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="200px" width="100%">
        <Spinner />
      </Box>
    );
  }

  if (!customerSubscription) {
    return (
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <SaldoPalommaCard />
        <Box display="flex" justifyContent="center" alignItems="center" minH="200px" width="100%">
          <Text color="#9F9DB0" textAlign="center">
            No tienes beneficios activos. Selecciona la suscripción al momento de pagar.
          </Text>
        </Box>
        <PuntosColombiaAccrualsTable />
      </Box>
    );
  }

  const { createdAt, puntosColombia, rewardInterval, price, hasBonda } = customerSubscription;
  const { rewardAmount } = PALOMMA_PLUS;
  const supportWhatsapp = '315 9212888';

  return (
    <>
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <SaldoPalommaCard />
        <Box
          border="1px solid"
          borderColor="#DFDEE2"
          borderRadius="8px"
          p={isMobile ? 4 : 5}
          display="flex"
          flexDirection="column"
          gap={4}
        >
          {/* Header: logo left, badge + price right */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between">
            <Image src={palommaPlusLogo} alt="Palomma Plus" maxH="28px" objectFit="contain" flexShrink={0} />
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
              <Badge colorScheme="green" fontSize="0.7rem">
                Activo
              </Badge>
              <Text fontSize="0.95rem" fontWeight={600}>
                {formatCurrency(price)}/mes
              </Text>
            </Box>
          </Box>

          {/* Benefits list */}
          <Box display="flex" flexDirection="column" gap={2} pl={0}>
            {puntosColombia > 0 && (
              <Box display="flex" gap={3} alignItems="flex-start">
                <Box flexShrink={0} mt="2px">
                  <IoHappyOutline size={18} color="#6B6A7A" />
                </Box>
                <Text fontSize="0.875rem" color="#6B6A7A" lineHeight={1.4}>
                  Recibe {puntosColombia} Puntos Colombia por el pago de tu arriendo
                </Text>
              </Box>
            )}
            <Box display="flex" gap={3} alignItems="flex-start">
              <Box flexShrink={0} mt="2px">
                <FiDollarSign size={18} color="#6B6A7A" />
              </Box>
              <Text fontSize="0.875rem" color="#6B6A7A" lineHeight={1.4}>
                Participa por {formatCurrency(rewardAmount)} para tu próximo arriendo. ¡Cada {rewardInterval} pagos hay
                un ganador!
              </Text>
            </Box>
            {hasBonda && (
              <Box display="flex" gap={3} alignItems="flex-start">
                <Box flexShrink={0} mt="2px">
                  <IoGiftOutline size={18} color="#6B6A7A" />
                </Box>
                <Text fontSize="0.875rem" color="#6B6A7A" lineHeight={1.4}>
                  Accede a 800+ descuentos y beneficios y ahorra hasta el 60% en marcas aliadas.{' '}
                  <Link href="https://palommaplus.com" isExternal color="purple.500" textDecoration="underline">
                    Ir a palommaplus.com
                  </Link>
                </Text>
              </Box>
            )}
          </Box>

          {puntosColombia > 0 && <PuntosColombiaAccrualsTable variant="inline" />}

          {/* Footer: activation date + cancel */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            pt={1}
            borderTop="1px solid"
            borderColor="#F0EFF2"
          >
            <Text fontSize="0.75rem" color="#9F9DB0">
              Activo desde {new Date(createdAt).toLocaleDateString('es-CO')}
            </Text>
            <Button size={isMobile ? 'xs' : 'sm'} variant="ghost" colorScheme="red" onClick={onOpen}>
              Cancelar
            </Button>
          </Box>
        </Box>

        {/* WhatsApp contact card */}
        <Box border="1px solid" borderColor="#DFDEE2" borderRadius="8px" p="3">
          <Text fontSize="0.7rem" color="#6B6A7A">
            ¿Quieres saber más sobre Palomma Plus?{' '}
            <Box as="span" whiteSpace="nowrap">
              Escríbenos al{' '}
              <Link
                href="https://wa.me/573159212888"
                isExternal
                display="inline-flex"
                alignItems="center"
                gap={1}
                verticalAlign="middle"
                _hover={{ textDecoration: 'none', opacity: 0.8 }}
              >
                <IoLogoWhatsapp size={15} />
                {supportWhatsapp}
              </Link>
            </Box>
          </Text>
        </Box>
      </Box>

      <SubscriptionCancellationModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={(reason) => cancelSubscription({ reason, isInPencilExperiment: experiments?.isInPencilExperiment })}
      />
    </>
  );
};

export default SubscriptionTab;
